import { useMemo, useState } from 'react';
import { DesktopGroup } from './DesktopGroup';
import { useSidebar } from '@/hooks/useSidebar';
import { useMenuConfig } from '@/hooks/useMenuConfig';
import { useTabs } from '@/hooks/useTabs';
import { DynamicIcon } from './DynamicIcon';
import { Skeleton } from '@/components/common/Skeleton';
import type { MenuItem } from '@/types';

export function AccordionMenu() {
  const { isExpanded, toggle } = useSidebar();
  const { data: menuConfig, isLoading } = useMenuConfig();
  const { tabs, activeTabId, openTab } = useTabs();
  const activeToolId = tabs.find((t) => t.id === activeTabId)?.toolConfig.id ?? null;
  const [expandedDesktops, setExpandedDesktops] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');

  const desktops = useMemo(() => menuConfig?.desktops ?? [], [menuConfig]);
  const trimmedFilter = filter.trim().toLowerCase();
  const isFiltering = trimmedFilter.length > 0;

  function toggleDesktop(desktopId: string) {
    setExpandedDesktops((prev) => {
      const next = new Set(prev);
      if (next.has(desktopId)) {
        next.delete(desktopId);
      } else {
        next.add(desktopId);
      }
      return next;
    });
  }

  function handleItemClick(item: MenuItem) {
    openTab(item, isFiltering ? { entry: 'search' } : undefined);
  }

  const visibleDesktops = useMemo(() => {
    const sorted = [...desktops].sort((a, b) => a.order - b.order);
    if (!isFiltering) return sorted;
    return sorted.filter((d) =>
      d.items.some((item) => item.label.toLowerCase().includes(trimmedFilter)),
    );
  }, [desktops, isFiltering, trimmedFilter]);

  const isInitialLoad = isLoading && !menuConfig;

  // Collapsed view: icon-only strip
  if (!isExpanded) {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <div className="h-[calc(--spacing(0.75))] shrink-0 bg-primary" />
        <div className="flex flex-1 flex-col overflow-y-auto py-1">
          <button
            onClick={toggle}
            className="mx-auto mb-1 flex h-8 w-8 items-center justify-center rounded text-white/30 hover:bg-white/5 hover:text-white/60"
            title="Expand sidebar"
          >
            <DynamicIcon name="panel-left-open" className="h-4 w-4" />
          </button>
          {isInitialLoad
            ? Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="mx-auto mb-1 h-6 w-6 rounded opacity-20" />
              ))
            : desktops.map((desktop) => (
                <button
                  key={desktop.id}
                  onClick={toggle}
                  title={desktop.label}
                  className="mx-auto flex h-8 w-8 items-center justify-center rounded text-white/30 hover:bg-white/5 hover:text-white/60"
                >
                  <DynamicIcon name={desktop.icon} className="h-4 w-4" />
                </button>
              ))}
        </div>
      </div>
    );
  }

  // Expanded view: search box + accordion (matches prototype ic-sb layout)
  return (
    <div className="flex h-full flex-col">
      <div className="h-[calc(--spacing(0.75))] shrink-0 bg-primary" />
      <div className="border-b border-white/[0.06] px-2.5 py-2.5">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search tools…"
          aria-label="Filter navigation items"
          className="w-full rounded-[calc(--spacing(1.25))] border border-white/10 bg-white/[0.06] px-2.5 py-1.5 text-xs text-white/70 placeholder:text-white/25 outline-none focus:border-primary/60"
        />
      </div>

      <div className="flex-1 overflow-y-auto py-1.5 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.1)_transparent]">
        {isInitialLoad ? (
          <div className="px-3 py-2" aria-label="Loading navigation" aria-busy="true">
            {[60, 75, 50, 80, 65, 70].map((width, i) => (
              <div key={i} className="mb-3 flex items-center gap-2">
                <Skeleton className="h-3.5 w-3.5 rounded" />
                <Skeleton className="h-3 rounded" style={{ width: `${width}%` }} />
              </div>
            ))}
          </div>
        ) : (
          <>
            {visibleDesktops.map((desktop) => (
              <DesktopGroup
                key={desktop.id}
                desktop={desktop}
                isExpanded={isFiltering ? true : expandedDesktops.has(desktop.id)}
                onToggle={() => toggleDesktop(desktop.id)}
                onItemClick={handleItemClick}
                filter={trimmedFilter}
                activeToolId={activeToolId}
              />
            ))}
            {isFiltering && visibleDesktops.length === 0 && (
              <p className="px-3 py-4 text-xs text-muted-foreground">No matches.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
